'use client';
import { useRef, useCallback, useEffect } from 'react';
import { createAriaHider } from './createAriaHider.js';
import { scopeTab } from './scopeTab.js';
import { FOCUS_SELECTOR, tabbable, focusable } from './tabbable.js';

var useFocusTrap = function(active) {
    if (active === void 0) active = true;
    var ref = useRef();
    var restoreAria = useRef(null);
    var focusNode = function(node) {
        var focusElement = node.querySelector("[data-autofocus]");
        if (!focusElement) {
            var children = Array.from(node.querySelectorAll(FOCUS_SELECTOR));
            focusElement = children.find(tabbable) || children.find(focusable) || null;
            if (!focusElement && focusable(node)) {
                focusElement = node;
            }
        }
        if (focusElement) {
            focusElement.focus({
                preventScroll: true
            });
        }
    };
    var setRef = useCallback(function(node) {
        if (!active) {
            return;
        }
        if (node === null) {
            if (restoreAria.current) {
                restoreAria.current();
                restoreAria.current = null;
            }
            return;
        }
        restoreAria.current = createAriaHider(node);
        if (ref.current === node) {
            return;
        }
        if (node) {
            /*
                 * Откладываем обработку узла на один frame.
                 * Это гарантирует корректное добавление фокуса.
                 * */ setTimeout(function() {
                if (node.getRootNode()) {
                    focusNode(node);
                }
            });
            ref.current = node;
        } else {
            ref.current = null;
        }
    }, [
        active
    ]);
    useEffect(function() {
        if (!active) {
            return undefined;
        }
        if (ref.current) {
            setTimeout(function() {
                return focusNode(ref.current);
            });
        }
        var handleKeyDown = function(event) {
            if (event.key === "Tab" && ref.current) {
                scopeTab(ref.current, event);
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return function() {
            document.removeEventListener("keydown", handleKeyDown);
            if (restoreAria.current) {
                restoreAria.current();
            }
        };
    }, [
        active
    ]);
    return setRef;
};

export { useFocusTrap };
